import React, { useEffect, useState, useRef } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

/**
 * WelcomeTour — first-visit pulse of 3 rotating tips on the Progress page.
 *
 * Shows once per user (gated by localStorage), auto-advances every 8s, and
 * can be dismissed manually. Doesn't reappear on subsequent visits.
 *
 * Visual cost: a small floating card pinned to the bottom-right of Progress
 * (or above the bottom nav on mobile). Backdrop-blurred, dismissible. No
 * full-screen overlay — user can keep tapping around the app while it's up.
 */

const SEEN_KEY = 'enferno_welcome_tour_seen_v1';
const AUTO_ADVANCE_MS = 8000;

const TIPS = [
  {
    title: 'Tap Strength to start training',
    body: 'Your Today\'s Strength card shows what\'s planned. Tap into Strength in the nav to log sets and finish your session.',
  },
  {
    title: 'Long-press cards to hide them',
    body: 'Don\'t care about a widget? Long-press (or tap-hold) any card to reveal a Hide option. Tweak more in Profile → Display Preferences.',
  },
  {
    title: 'Coach is always one tap away',
    body: 'Ask anything — exercise form, why today\'s session looks the way it does, recovery questions. Coach is the chat icon in the nav.',
  },
];

const WelcomeTour = () => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const advanceTimer = useRef(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(SEEN_KEY) === '1') return;
      // Defer mount a beat so it doesn't fight with page render
      const t = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(t);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      if (index < TIPS.length - 1) setIndex(index + 1);
      else dismiss();
    }, AUTO_ADVANCE_MS);
    return () => clearTimeout(advanceTimer.current);
  }, [open, index]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;
  const tip = TIPS[index];

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 bottom-20 sm:bottom-6 w-[calc(100%-32px)] max-w-md pointer-events-auto"
      data-testid="welcome-tour"
    >
      <div className="bg-enf-surface-deep/95 backdrop-blur-md border border-enf-surface-border rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
        {/* Top: indicator dots + close */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex gap-1.5">
            {TIPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === index ? 'bg-enf-accent w-6' : i < index ? 'bg-enf-surface-elevated w-3' : 'bg-enf-surface-elevated w-3'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss welcome tour"
            className="p-1 text-enf-text-muted hover:text-enf-text-primary transition-colors"
            data-testid="welcome-tour-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1.5">
            Welcome to Enferno
          </div>
          <div className="font-display text-lg tracking-wide leading-tight mb-1.5">{tip.title}</div>
          <p className="text-xs text-enf-text-secondary leading-relaxed">{tip.body}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-enf-surface-border bg-enf-surface-mid/30">
          <button
            type="button"
            onClick={() => index > 0 && setIndex(index - 1)}
            disabled={index === 0}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-enf-text-secondary hover:text-enf-text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="welcome-tour-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="text-[10px] uppercase tracking-widest text-enf-text-muted">
            {index + 1} / {TIPS.length}
          </div>
          {index < TIPS.length - 1 ? (
            <button
              type="button"
              onClick={() => setIndex(index + 1)}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-enf-text-primary bg-enf-surface-elevated hover:bg-enf-surface-elevated rounded transition-colors"
              data-testid="welcome-tour-next"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-enf-text-primary bg-enf-accent hover:bg-enf-accent-hover rounded transition-colors"
              data-testid="welcome-tour-finish"
            >
              Got it
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
